/**
 * Spec 055, step 3: «+ Добавить» — a shared note or a registry skill found by name, a page at a
 * time; thousands are never loaded at once. What is already granted is not offered. A skill shows
 * its SkillSpector verdict, and one the place does not take is greyed out: a unit's kit takes
 * checked skills only, a card «осторожно» too (with the person's tick), «заблокирован» never.
 */

import { useState } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";

import { KitSearch, type FoundNote, type FoundSkill } from "@/api/units";
import { Button } from "@/components/ui/Button";
import { TextField } from "@/components/ui/Field";
import { VERDICT_LABEL } from "@/lib/agents";

export const KIT_VERDICTS: readonly string[] = ["safe", "accepted"];
export const CARD_VERDICTS: readonly string[] = ["safe", "accepted", "caution"];

export function verdictNote(verdict: FoundSkill["verdict"]): string {
  return verdict ? VERDICT_LABEL[verdict] : "нет записи SkillSpector";
}

export function NoteSearch({ taken, onPick }: { taken: string[]; onPick: (note: FoundNote) => void }) {
  const [q, setQ] = useState("");
  const query = q.trim();
  const found = useInfiniteQuery({
    queryKey: ["kit-search", "notes", query],
    queryFn: ({ pageParam }) => KitSearch.notes(query, pageParam),
    initialPageParam: 0,
    getNextPageParam: (last) => last.next_offset ?? undefined,
  });
  const items = (found.data?.pages ?? []).flatMap((page) => page.items).filter((n) => !taken.includes(n.id));
  return (
    <div className="flex flex-col gap-2 rounded-card border border-p-border bg-p-surface px-3 py-3" data-note-search>
      <TextField label="Найти общую заметку" placeholder="по названию" value={q} onChange={(e) => setQ(e.target.value)} />
      {found.isLoading && <span className="text-meta text-p-text-muted">Ищем…</span>}
      {items.map((note) => (
        <div key={note.id} className="flex items-center justify-between gap-2">
          <span className="min-w-0 flex-1 break-words text-body text-p-text">{note.title}</span>
          <Button size="compact" variant="secondary" onClick={() => onPick(note)}>Добавить</Button>
        </div>
      ))}
      {found.isSuccess && !items.length && <span className="text-meta text-p-text-muted">Ничего не нашлось.</span>}
      {found.hasNextPage && (
        <Button size="compact" variant="ghost" loading={found.isFetchingNextPage} onClick={() => void found.fetchNextPage()}>Ещё</Button>
      )}
    </div>
  );
}

export function SkillSearch({ taken, allowed, onPick }: {
  taken: string[];
  allowed: readonly string[];
  onPick: (skill: FoundSkill) => void;
}) {
  const [q, setQ] = useState("");
  const query = q.trim();
  const found = useInfiniteQuery({
    queryKey: ["kit-search", "skills", query],
    queryFn: ({ pageParam }) => KitSearch.skills(query, pageParam),
    initialPageParam: 0,
    getNextPageParam: (last) => last.next_offset ?? undefined,
  });
  const items = (found.data?.pages ?? []).flatMap((page) => page.items).filter((s) => !taken.includes(s.slug));
  return (
    <div className="flex flex-col gap-2 rounded-card border border-p-border bg-p-surface px-3 py-3" data-skill-search>
      <TextField label="Найти навык" placeholder="по слагу или имени" value={q} onChange={(e) => setQ(e.target.value)} />
      {found.isLoading && <span className="text-meta text-p-text-muted">Ищем…</span>}
      {items.map((skill) => {
        const ok = skill.verdict !== null && allowed.includes(skill.verdict);
        return (
          <div key={skill.slug} className="flex items-center justify-between gap-2">
            <span className="min-w-0 flex-1">
              <span className="block break-words text-body text-p-text">{skill.slug}</span>
              <span className="block text-micro text-p-text-muted">{verdictNote(skill.verdict)}</span>
            </span>
            <Button size="compact" variant="secondary" disabled={!ok} onClick={() => onPick(skill)}>Добавить</Button>
          </div>
        );
      })}
      {found.isSuccess && !items.length && <span className="text-meta text-p-text-muted">Ничего не нашлось.</span>}
      {found.hasNextPage && (
        <Button size="compact" variant="ghost" loading={found.isFetchingNextPage} onClick={() => void found.fetchNextPage()}>Ещё</Button>
      )}
    </div>
  );
}
